import CryptoJS from 'crypto-js';
import builder from 'xmlbuilder';
import fileDownload from 'js-file-download';
import { SECRET } from '../config/settings';
import { DEMO } from './staticData';
import gameService from '../services/game.service';

/**
 * Maps API response field names to standardized field names used by UI components.
 * This function handles the discrepancy between database function return column names
 * and the expected UI field names for player statistics.
 * 
 * @param {Object} stats - The raw stats object from the API response
 * @returns {Object} - Normalized stats object with consistent field names
 */
export const mapPlayerStatsData = (stats) => {
    if (!stats) return null;

    // Calculate percentage minutes - handle edge cases
    const calcPercentageMinutes = (minutes, games) => {
        const min = Number(minutes) || 0;
        const gameCount = Number(games) || 0;
        if (gameCount === 0 || min === 0) return 0;

        // Calculate percentage based on actual average minutes per game
        const avgMinPerGame = min / gameCount;
        // Cap at 100% since a player can't play more than 100% of a game
        const percentage = Math.min(Math.round((avgMinPerGame / 90) * 100), 100);
        return percentage;
    };

    // Helper to calculate per-game average (returns number with 1 decimal)
    const calcAverage = (total, games) => {
        const t = Number(total) || 0;
        const g = Number(games) || 0;
        if (g === 0 || t === 0) return 0;
        return Math.round((t / g) * 10) / 10; // 1 decimal place
    };

    // Get total minutes - API may return as games_minutes, total_games_minutes, or total_games_player_minutes
    const totalMinutes = Number(stats.games_minutes) || Number(stats.total_games_minutes) || Number(stats.total_games_player_minutes) || 0;
    // Get total games - API may return as total_player_games, total_games_player_played, or total_game
    const totalGames = Number(stats.total_player_games) || Number(stats.total_games_player_played) || Number(stats.total_game) || 0;
    // Get games starting - API may return as games_starting or total_games_starting
    const gamesStarting = Number(stats.games_starting) || Number(stats.total_games_starting) || Number(stats.total_games_player_starting) || 0;
    // Get average minutes
    const avgMinutes = Number(stats.average_games_minutes) || Number(stats.average_games_player_minutes) || (totalGames > 0 ? Math.floor(totalMinutes / totalGames) : 0);

    // Selected games count for per-game average calculations
    const selectedGames = Number(stats.selected_games_player_played) || totalGames;

    // Get total values directly from SQL column names for average calculations
    const totalGoals = Number(stats.total_goals) || 0;
    const totalShots = Number(stats.total_shots) || 0;
    const totalPasses = Number(stats.total_passes) || 0;
    const totalPassesSuccess = Number(stats.total_passes_success) || 0;
    const totalPassesFail = Number(stats.total_passes_fail) || 0;
    const totalTackles = Number(stats.total_tackles) || 0;
    const totalTacklesSuccess = Number(stats.total_tackles_success) || 0;
    const totalInterceptions = Number(stats.total_interceptions) || 0;
    const totalClearances = Number(stats.total_clearances) || 0;
    const totalBlocks = Number(stats.total_blocks) || 0;
    const totalFouls = Number(stats.total_fouls) || 0;
    const totalYellowCards = Number(stats.total_yellow_cards) || 0;
    const totalRedCards = Number(stats.total_red_cards) || 0;
    const totalDribbles = Number(stats.total_dribbles) || 0;
    const totalDribblesSuccess = Number(stats.total_dribbles_success) || 0;
    const totalDribblesFail = Number(stats.total_dribbles_fail) || 0;
    const totalCrosses = Number(stats.total_crosses) || 0;
    const totalCrossesSuccess = Number(stats.total_crosses_success) || 0;
    const totalFreeKicks = Number(stats.total_free_kicks) || 0;
    const totalCorner = Number(stats.total_corner) || 0;
    const totalPenalties = Number(stats.total_penalties) || 0;
    const totalPenaltiesMissed = Number(stats.total_penalties_missed) || 0;
    const totalShotsOnTarget = Number(stats.total_shots_on_target) || 0;
    const totalShotsOffTarget = Number(stats.total_shots_off_target) || 0;
    const totalShotsInTheBox = Number(stats.total_shots_in_the_box) || 0;
    const totalShotsOutOfTheBox = Number(stats.total_shots_out_of_the_box) || 0;
    const totalKeyPasses = Number(stats.total_key_passes) || 0;
    const totalKeyPassesSuccessful = Number(stats.total_key_passes_successful) || 0;
    const totalThroughPasses = Number(stats.total_through_passes) || 0;
    const totalThroughPassesSuccess = Number(stats.total_through_passes_success) || 0;
    const totalPassesForShots = Number(stats.total_passes_for_shots) || 0;
    const totalAssists = Number(stats.total_assists) || 0;
    const totalTurnovers = Number(stats.total_turnovers) || 0;
    const totalTurnoversOnOffensiveCourt = Number(stats.total_turnovers_on_offensive_court) || 0;
    const totalTurnoversOnDefensiveCourt = Number(stats.total_turnovers_on_defensive_court) || 0;
    const totalOffsides = Number(stats.total_offsides) || 0;
    const totalDrawFouls = Number(stats.total_draw_fouls) || 0;
    const totalSaved = Number(stats.total_saved) || 0;
    const totalOwnGoals = Number(stats.total_own_goals) || 0;
    // Court-specific stats
    const totalInterceptionOnOffensiveCourt = Number(stats.total_interception_on_offensive_court) || 0;
    const totalInterceptionOnDefensiveCourt = Number(stats.total_interception_on_defensive_court) || 0;
    const totalTackleOnOffensiveCourt = Number(stats.total_tackle_on_offensive_court) || 0;
    const totalTackleOnDefensiveCourt = Number(stats.total_tackle_on_defensive_court) || 0;
    // Pass types
    const totalShortPasses = Number(stats.total_short_passes) || 0;
    const totalLongPasses = Number(stats.total_long_passes) || 0;
    const totalLongPassesSuccess = Number(stats.total_long_passes_success) || 0;
    const totalFreekickPasses = Number(stats.total_freekick_passes) || 0;
    const totalThrowinPasses = Number(stats.total_throwin_passes) || 0;
    // Goalkeeper stats
    const totalSuperSaves = Number(stats.total_super_saves) || Number(stats.total_super_save) || 0;
    const totalBuildUps = Number(stats.total_build_ups) || 0;
    const totalGoalkeeperExit = Number(stats.total_goalkeeper_exit) || 0;
    const totalAirChallenge = Number(stats.total_air_challenge) || 0;
    const totalGroundChallenge = Number(stats.total_ground_challenge) || 0;
    const totalOneVsOne = Number(stats.total_one_vs_one) || 0;
    const totalGoalsReceived = Number(stats.total_goals_received) || Number(stats.total_goal_received) || 0;
    const totalGoalKicks = Number(stats.total_goal_kicks) || 0;
    const totalPunches = Number(stats.total_punches) || 0;
    const totalCatches = Number(stats.total_catches) || 0;
    const totalThrows = Number(stats.total_throws) || 0;
    const cleanSheets = Number(stats.clean_sheets) || 0;
    // Opponent stats
    const totalOpponentCrosses = Number(stats.total_opponent_crosses) || 0;
    const totalOpponentCorners = Number(stats.total_opponent_corners) || 0;
    const totalOpponentFreeKicks = Number(stats.total_opponent_free_kicks) || 0;

    return {
        ...stats,
        // Profile information
        player_first_name: stats.player_full_name ? stats.player_full_name.split(' ')[0] : stats.player_first_name || '',
        player_last_name: stats.player_full_name ? stats.player_full_name.split(' ').slice(1).join(' ') : stats.player_last_name || '',
        player_name: stats.player_full_name || stats.player_name || '',
        player_birth_date: stats.player_date_of_birth || stats.player_birth_date || null,
        player_position: stats.player_position_name || stats.player_position || '',
        player_second_position: stats.player_second_position_name || stats.player_second_position || '',
        player_role_one: stats.player_position_role_one_name || stats.player_role_one || '',
        player_role_two: stats.player_position_role_two_name || stats.player_role_two || '',
        player_height: stats.player_height || null,
        player_weight: stats.player_weight || null,
        player_foot: stats.player_strong_foot || stats.player_foot || null,
        player_age_group: stats.player_age_group_name || stats.player_age_group || '',
        player_photo: stats.player_image || stats.player_photo || '',
        jersey_number: stats.player_jersey_number || stats.jersey_number || null,
        team_name: stats.team_name || '',
        season_name: stats.season_name || '',
        team_image: stats.team_image || stats.team_logo || '',
        // Total/All games data
        total_games_player_played: totalGames,
        total_games_player_minutes: totalMinutes,
        total_games_player_starting: gamesStarting,
        average_games_minutes: avgMinutes,
        total_games_player_average_minutes: avgMinutes,
        total_games_player_percentage_minutes: calcPercentageMinutes(totalMinutes, totalGames),
        // Selected/Filtered games data
        selected_games_player_played: selectedGames,
        selected_games_player_minutes: stats.selected_games_player_minutes ?? totalMinutes,
        selected_games_player_starting: stats.selected_games_player_starting ?? gamesStarting,
        selected_games_player_average_minutes: selectedGames > 0 ? Math.floor((stats.selected_games_player_minutes ?? totalMinutes) / selectedGames) : avgMinutes,
        selected_games_player_percentage_minutes: calcPercentageMinutes(stats.selected_games_player_minutes ?? totalMinutes, selectedGames),
        // Team results - use available fields
        total_games_won: stats.total_games_won !== undefined ? stats.total_games_won : 0,
        total_games_draw: stats.total_games_draw !== undefined ? stats.total_games_draw : 0,
        total_games_lose: stats.total_games_lose !== undefined ? stats.total_games_lose : 0,
        team_total_games: stats.team_total_games !== undefined ? stats.team_total_games : totalGames,
        games_selected: selectedGames,
        // Player stats - using exact SQL column names with averages calculated
        // Core stats
        total_goals: totalGoals,
        average_goals: calcAverage(totalGoals, selectedGames),
        total_shots: totalShots,
        average_shots: calcAverage(totalShots, selectedGames),
        total_shots_on_target: totalShotsOnTarget,
        average_shots_on_target: calcAverage(totalShotsOnTarget, selectedGames),
        total_shots_off_target: totalShotsOffTarget,
        average_shots_off_target: calcAverage(totalShotsOffTarget, selectedGames),
        total_shots_in_the_box: totalShotsInTheBox,
        average_shots_in_the_box: calcAverage(totalShotsInTheBox, selectedGames),
        total_shots_out_of_the_box: totalShotsOutOfTheBox,
        average_shots_out_of_the_box: calcAverage(totalShotsOutOfTheBox, selectedGames),
        total_assists: totalAssists,
        average_assists: calcAverage(totalAssists, selectedGames),
        total_penalties: totalPenalties,
        average_penalties: calcAverage(totalPenalties, selectedGames),
        total_penalties_missed: totalPenaltiesMissed,
        average_penalties_missed: calcAverage(totalPenaltiesMissed, selectedGames),
        total_own_goals: totalOwnGoals,
        average_own_goals: calcAverage(totalOwnGoals, selectedGames),
        // Passes
        total_passes: totalPasses,
        average_passes: calcAverage(totalPasses, selectedGames),
        total_passes_success: totalPassesSuccess,
        average_passes_success: calcAverage(totalPassesSuccess, selectedGames),
        total_passes_fail: totalPassesFail,
        average_passes_fail: calcAverage(totalPassesFail, selectedGames),
        total_key_passes: totalKeyPasses,
        average_key_passes: calcAverage(totalKeyPasses, selectedGames),
        total_key_passes_successful: totalKeyPassesSuccessful,
        average_key_passes_successful: calcAverage(totalKeyPassesSuccessful, selectedGames),
        total_through_passes: totalThroughPasses,
        average_through_passes: calcAverage(totalThroughPasses, selectedGames),
        total_through_passes_success: totalThroughPassesSuccess,
        average_through_passes_success: calcAverage(totalThroughPassesSuccess, selectedGames),
        total_passes_for_shots: totalPassesForShots,
        average_passes_for_shots: calcAverage(totalPassesForShots, selectedGames),
        total_short_passes: totalShortPasses,
        average_short_passes: calcAverage(totalShortPasses, selectedGames),
        total_long_passes: totalLongPasses,
        average_long_passes: calcAverage(totalLongPasses, selectedGames),
        total_long_passes_success: totalLongPassesSuccess,
        average_long_passes_success: calcAverage(totalLongPassesSuccess, selectedGames),
        total_freekick_passes: totalFreekickPasses,
        average_freekick_passes: calcAverage(totalFreekickPasses, selectedGames),
        total_throwin_passes: totalThrowinPasses,
        average_throwin_passes: calcAverage(totalThrowinPasses, selectedGames),
        // Dribbles
        total_dribbles: totalDribbles,
        average_dribbles: calcAverage(totalDribbles, selectedGames),
        total_dribbles_success: totalDribblesSuccess,
        average_dribbles_success: calcAverage(totalDribblesSuccess, selectedGames),
        total_dribbles_fail: totalDribblesFail,
        average_dribbles_fail: calcAverage(totalDribblesFail, selectedGames),
        // Crosses & Corners
        total_crosses: totalCrosses,
        average_crosses: calcAverage(totalCrosses, selectedGames),
        total_crosses_success: totalCrossesSuccess,
        average_crosses_success: calcAverage(totalCrossesSuccess, selectedGames),
        total_corner: totalCorner,
        average_corner: calcAverage(totalCorner, selectedGames),
        total_free_kicks: totalFreeKicks,
        average_free_kicks: calcAverage(totalFreeKicks, selectedGames),
        // Turnovers
        total_turnovers: totalTurnovers,
        average_turnovers: calcAverage(totalTurnovers, selectedGames),
        total_turnovers_on_offensive_court: totalTurnoversOnOffensiveCourt,
        average_turnovers_on_offensive_court: calcAverage(totalTurnoversOnOffensiveCourt, selectedGames),
        total_turnovers_on_defensive_court: totalTurnoversOnDefensiveCourt,
        average_turnovers_on_defensive_court: calcAverage(totalTurnoversOnDefensiveCourt, selectedGames),
        total_offsides: totalOffsides,
        average_offsides: calcAverage(totalOffsides, selectedGames),
        // Defensive stats
        total_tackles: totalTackles,
        average_tackles: calcAverage(totalTackles, selectedGames),
        total_tackles_success: totalTacklesSuccess,
        average_tackles_success: calcAverage(totalTacklesSuccess, selectedGames),
        total_tackle_on_offensive_court: totalTackleOnOffensiveCourt,
        average_tackle_on_offensive_court: calcAverage(totalTackleOnOffensiveCourt, selectedGames),
        total_tackle_on_defensive_court: totalTackleOnDefensiveCourt,
        average_tackle_on_defensive_court: calcAverage(totalTackleOnDefensiveCourt, selectedGames),
        total_interceptions: totalInterceptions,
        average_interceptions: calcAverage(totalInterceptions, selectedGames),
        total_interception_on_offensive_court: totalInterceptionOnOffensiveCourt,
        average_interception_on_offensive_court: calcAverage(totalInterceptionOnOffensiveCourt, selectedGames),
        total_interception_on_defensive_court: totalInterceptionOnDefensiveCourt,
        average_interception_on_defensive_court: calcAverage(totalInterceptionOnDefensiveCourt, selectedGames),
        total_clearances: totalClearances,
        average_clearances: calcAverage(totalClearances, selectedGames),
        total_blocks: totalBlocks,
        average_blocks: calcAverage(totalBlocks, selectedGames),
        total_saved: totalSaved,
        average_saved: calcAverage(totalSaved, selectedGames),
        // Fouls & Cards
        total_fouls: totalFouls,
        average_fouls: calcAverage(totalFouls, selectedGames),
        total_draw_fouls: totalDrawFouls,
        average_draw_fouls: calcAverage(totalDrawFouls, selectedGames),
        total_yellow_cards: totalYellowCards,
        average_yellow_cards: calcAverage(totalYellowCards, selectedGames),
        total_red_cards: totalRedCards,
        average_red_cards: calcAverage(totalRedCards, selectedGames),
        // Goalkeeper specific stats
        total_super_saves: totalSuperSaves,
        average_super_saves: calcAverage(totalSuperSaves, selectedGames),
        total_build_ups: totalBuildUps,
        average_build_ups: calcAverage(totalBuildUps, selectedGames),
        total_goalkeeper_exit: totalGoalkeeperExit,
        average_goalkeeper_exit: calcAverage(totalGoalkeeperExit, selectedGames),
        total_air_challenge: totalAirChallenge,
        average_air_challenge: calcAverage(totalAirChallenge, selectedGames),
        total_ground_challenge: totalGroundChallenge,
        average_ground_challenge: calcAverage(totalGroundChallenge, selectedGames),
        total_one_vs_one: totalOneVsOne,
        average_one_vs_one: calcAverage(totalOneVsOne, selectedGames),
        total_goals_received: totalGoalsReceived,
        average_goals_received: calcAverage(totalGoalsReceived, selectedGames),
        total_goal_kicks: totalGoalKicks,
        average_goal_kicks: calcAverage(totalGoalKicks, selectedGames),
        total_punches: totalPunches,
        average_punches: calcAverage(totalPunches, selectedGames),
        total_catches: totalCatches,
        average_catches: calcAverage(totalCatches, selectedGames),
        total_throws: totalThrows,
        average_throws: calcAverage(totalThrows, selectedGames),
        clean_sheets: cleanSheets,
        average_clean_sheets: calcAverage(cleanSheets, selectedGames),
        // Opponent stats (for goalkeepers)
        total_opponent_crosses: totalOpponentCrosses,
        average_opponent_crosses: calcAverage(totalOpponentCrosses, selectedGames),
        total_opponent_corners: totalOpponentCorners,
        average_opponent_corners: calcAverage(totalOpponentCorners, selectedGames),
        total_opponent_free_kicks: totalOpponentFreeKicks,
        average_opponent_free_kicks: calcAverage(totalOpponentFreeKicks, selectedGames),
        // Games
        total_player_games: stats.total_player_games || totalGames,
        average_player_games: 0,
        // Aliases for statList/goalkeeper IDs that use singular or alternate naming
        total_goal: totalGoals,
        average_goal: calcAverage(totalGoals, selectedGames),
        total_shot: totalShots,
        average_shot: calcAverage(totalShots, selectedGames),
        total_shot_on_target: totalShotsOnTarget,
        average_shot_on_target: calcAverage(totalShotsOnTarget, selectedGames),
        total_shot_off_target: totalShotsOffTarget,
        average_shot_off_target: calcAverage(totalShotsOffTarget, selectedGames),
        total_shot_on_box: totalShotsInTheBox,
        average_shot_on_box: calcAverage(totalShotsInTheBox, selectedGames),
        total_shot_out_of_box: totalShotsOutOfTheBox,
        average_shot_out_of_box: calcAverage(totalShotsOutOfTheBox, selectedGames),
        total_dribble: totalDribbles,
        average_dribble: calcAverage(totalDribbles, selectedGames),
        total_dribble_successful: totalDribblesSuccess,
        average_dribble_successful: calcAverage(totalDribblesSuccess, selectedGames),
        total_free_kick: totalFreeKicks,
        average_free_kick: calcAverage(totalFreeKicks, selectedGames),
        total_successful_passes: totalPassesSuccess,
        average_successful_passes: calcAverage(totalPassesSuccess, selectedGames),
        total_turnover: totalTurnovers,
        average_turnover: calcAverage(totalTurnovers, selectedGames),
        total_offside: totalOffsides,
        average_offside: calcAverage(totalOffsides, selectedGames),
        total_tackle: totalTackles,
        average_tackle: calcAverage(totalTackles, selectedGames),
        total_interception: totalInterceptions,
        average_interception: calcAverage(totalInterceptions, selectedGames),
        total_clearance: totalClearances,
        average_clearance: calcAverage(totalClearances, selectedGames),
        total_blocked: totalBlocks,
        average_blocked: calcAverage(totalBlocks, selectedGames),
        // Goalkeeper aliases
        total_super_save: totalSuperSaves,
        average_super_save: calcAverage(totalSuperSaves, selectedGames),
        total_goal_received: totalGoalsReceived,
        average_goal_received: calcAverage(totalGoalsReceived, selectedGames)
    };
};

export const createCommand = async (tagList, name) => {
    let rawVideoList = [...new Set(tagList.map((tag) => tag.video_url))];
    let videoList = await Promise.all(
        rawVideoList.map(async (url) => {
            if (url?.includes('youtube.com') || url?.includes('youtu.be')) {
                return (await gameService.getAsyncNewStreamURL(url)).result.video_url;
            }
            return url;
        })
    );

    let videos = videoList.map((tag, i) => {
        return {
            url: tag,
            SecondBoxText: tagList[0]?.player_fname ? `#${tagList[0]?.jersey} ${tagList[0]?.player_fname} ${tagList[0]?.player_lname}` : name
        };
    });

    let clips = tagList.map((tag, i) => {
        return {
            Video: rawVideoList.indexOf(tag.video_url) + 1,
            Trim: `${toSecond(tag.start_time)}:${toSecond(tag.end_time)}`,
            FirstBoxText: `${tag.action_name ?? 'Team Actions'}`
        };
    });

    let obj = {
        Render: {
            FileData: {
                Name: name,
                Format: 'mp4',
                Resolution: '1280x720',
                FPS: '60',
                Preset: 'ultrafast',
                FontFile: 'ArialBold.ttf',
                FontColor: '#ffffff',
                FontSize: '35',
                FirstBoxSize: '300x60',
                FirstBoxColor: '#808080@0.7',
                FirstBoxFormat: 'rgba',
                SeconBoxSize: '500x60',
                SecondBoxColor: '#FFA500@0.7',
                SecondBoxFormat: 'rgba',
                LogoURL: '/LogoforLightBackground1.png'
            },
            Videos: { Video: videos },
            Clips: { Clip: clips }
        }
    };

    const command = builder.create(obj).end({ pretty: true });

    // const command = `ffmpeg - y - i "${url}" - f lavfi - i color = color =#808080@0.7: size = 300x60, format = rgba - f lavfi - i color = color = #FFA500@0.7: size = ${ !tagList[0]?.player_fname ? "540" : "340" } x60, format = rgba - i "https://s4usitesimages.s3.amazonaws.com/images/JustSmallLogo.png" - filter_complex "${tagList.map((tag, i) =>
    //   `[0:v]trim=${toSecond(tag.start_time)}:${toSecond(tag.end_time)},setpts=PTS-STARTPTS[v${i}];[1:v]drawtext=text='${tag.action_name ?? "Team Actions"}':fontfile=ArialBold.ttf:x=(w-text_w)/2:y=(h-text_h)/2:fontsize=37:fontcolor=white[trans_gray_tx${i ? i : ''}];[v${i}][trans_gray_tx${i ? i : ''}]overlay=shortest=1:x=20:y=H-h-20[v_${i}];[0:a]atrim=${toSecond(tag.start_time)}:${toSecond(tag.end_time)},asetpts=PTS-STARTPTS[a${i}];`).join("")
    //   }${tagList.map((tag, i) => `[v_${i}][a${i}]`).join("")
    //   }concat=n=${tagList.length}:v=1:a=1[outv][outa];[2:v]drawtext=text='${!tagList[0]?.player_fname ? name : `#${tagList[0]?.jersey} ${tagList[0]?.player_fname}`}':fontfile=ArialBold.ttf:x=(w-text_w)/2:y=(h-text_h)/2:fontsize=37:fontcolor=white[trans_org_tx];[outv][trans_org_tx]overlay=shortest=1:x=262:y=H-h-20[v_org];[v_org][3:v]overlay=W-w-20:H-h-20[v_t_logo]" -map "[v_t_logo]" -map "[outa]" -preset superfast "${name}.mp4"`

    fileDownload(command, `${name}.xml`);
};

export function toHHMMSS(data) {
    if (!data || data === '') return '00:00:00';
    let sec_num = parseInt(data, 10); // don't forget the second param
    if (sec_num < 0) sec_num = 0;
    let hours = Math.floor(sec_num / 3600);
    let minutes = Math.floor((sec_num - hours * 3600) / 60);
    let seconds = sec_num - hours * 3600 - minutes * 60;

    if (hours < 10) {
        hours = '0' + hours;
    }
    if (minutes < 10) {
        minutes = '0' + minutes;
    }
    if (seconds < 10) {
        seconds = '0' + seconds;
    }
    return hours + ':' + minutes + ':' + seconds;
}

export function addSecToHHMMSS(str, sec) {
    return toHHMMSS(toSecond(str) + sec);
}

export function subSecToHHMMSS(str, sec) {
    return toHHMMSS(toSecond(str) - sec);
}

export function toSecond(data) {
    if (!data || data === '') return 0;
    let a = data.split(':'); // split it at the colons

    // minutes are worth 60 seconds. Hours are worth 60 minutes.
    return +a[0] * 60 * 60 + +a[1] * 60 + +a[2];
}
export function getUser() {
    try {
        const user = localStorage.getItem('user') ? JSON.parse(CryptoJS.AES.decrypt(localStorage.getItem('user'), SECRET).toString(CryptoJS.enc.Utf8)) : null;
        return user;
    } catch (e) {
        console.error('loading user error');
        return null;
    }
}
export function setUser(user) {
    try {
        localStorage.setItem('user', CryptoJS.AES.encrypt(JSON.stringify(user), SECRET).toString());
    } catch (e) {
        console.error('saving user error');
    }
}

export function parseJwt(token) {
    try {
        return JSON.parse(atob(token.split('.')[1]));
    } catch (e) {
        return null;
    }
}

export function divideTags(tagList) {
    let actions = {};
    tagList.forEach((tag) => {
        const actionKey = tag.action_name;
        const typeKey = tag.action_type_name;
        let success = actions?.[actionKey]?.[typeKey]?.success ?? [];
        let unsuccess = actions?.[actionKey]?.[typeKey]?.unsuccess ?? [];

        if (DEMO?.[actionKey]?.success.includes(tag.action_result_name)) {
            success = [...success, tag];
        } else {
            unsuccess = [...unsuccess, tag];
        }

        actions = {
            ...actions,
            [actionKey]: {
                ...actions?.[actionKey],
                [typeKey]: { success, unsuccess }
            }
        };
    });
    return actions;
}

export function filterSuccessTags(tagList) {
    let actions = {};
    tagList.forEach((tag) => {
        const actionKey = tag.action_name;
        let success = actions?.[actionKey] ?? [];

        if (DEMO?.[actionKey]?.success.includes(tag.action_result_name)) {
            success = [...success, tag];
            actions = {
                ...actions,
                [actionKey]: [success]
            };
        }
    });
    return actions;
}

export function getPercent(value, max) {
    return (value * 100) / max;
}

export function manualFilterForTags(tagList, playerId, t) {
    const filterData = [
        {
            title: t('Goal'),
            action: 1,
            action_type: null,
            action_result: [3]
        },
        {
            title: t('Assist'),
            action: 9,
            action_type: null,
            action_result: null
        },
        {
            title: t('Shot'),
            action: 1,
            action_type: null,
            action_result: [1]
        },
        {
            title: t('Cross'),
            action: 3,
            action_type: null,
            action_result: [4]
        },
        {
            title: t('Dribble'),
            action: 4,
            action_type: null,
            action_result: [4]
        },
        {
            title: t('ThroughPass'),
            action: 2,
            action_type: 6,
            action_result: [4]
        },
        {
            title: t('ShortPass'),
            action: 2,
            action_type: 4,
            action_result: [4]
        },
        {
            title: t('LongPass'),
            action: 2,
            action_type: 5,
            action_result: [4]
        },
        {
            title: t('KeyPass'),
            action: 2,
            action_type: 7,
            action_result: [4]
        },
        {
            title: t('HeaderPass'),
            action: 2,
            action_type: 3,
            action_result: [4]
        },
        {
            title: t('DrawFoul'),
            action: 6,
            action_type: null,
            action_result: [13, 14]
        },
        {
            title: t('Interception'),
            action: 10,
            action_type: null,
            action_result: null
        },
        {
            title: t('Clearance'),
            action: 11,
            action_type: null,
            action_result: null
        },
        {
            title: t('Saved'),
            action: 8,
            action_type: null,
            action_result: null
        },
        {
            title: t('ThrowIn'),
            action: 2,
            action_type: 14,
            action_result: [4]
        }
    ];
    return filterData
        .map((f) => {
            const total = tagList.filter(
                (tag) =>
                    tag.action_id === f.action &&
                    (f.action_type === null ? true : tag.action_type_id === f.action_type) &&
                    (f.action_result === null ? true : f.action_result.includes(tag.action_result_id))
            );
            return {
                title: f.title,
                success: total.filter((f) => f.player_id === playerId),
                total: total.length
            };
        })
        .filter((f) => f.success.length > 0);
}
export function filterAllTags(tagList, playerId) {
    let actions = {};
    const playerTag = tagList.filter((f) => f.player_id === playerId);
    playerTag.forEach((tag) => {
        const actionKey = tag.action_name;

        actions = {
            ...actions,
            [actionKey]: !actions?.[actionKey]
                ? {
                      total: 1,
                      success: []
                  }
                : {
                      ...actions?.[actionKey],
                      total: actions?.[actionKey].total + 1
                  }
        };

        let success = actions?.[actionKey]?.success ?? [];

        if (tag.player_id === playerId && DEMO?.[actionKey]?.success.includes(tag.action_result_name)) {
            success = [...success, tag];
            actions = {
                ...actions,
                [actionKey]: {
                    ...actions?.[actionKey],
                    success
                }
            };
        }
    });

    // then sort by success length

    return Object.keys(actions)
        .map((key) => {
            return { ...actions[key], action: key };
        })
        .sort((a, b) => b.total - a.total);
}

export const getSum = (dataArr) => {
    if (!Array.isArray(dataArr) || dataArr.length === 0) return 0;
    return dataArr.reduce((sum, val) => sum + (Number(val) || 0), 0);
};

export const getList = (series) => {

    if (series) {
        const llst = []
        const slst = []
        const clst = []
        series.forEach(element => {
            if (element?.data > 0) {
                llst.push(element.name)
                slst.push(element.data)
                clst.push(element.color)
            }
        });
        return { seriesDonut: slst, labelsLst: llst, colorLst: clst }
    }
}


export const getSeriesData = (playerData) => {

    // 1v1 Attacking - player's dribble attempts (action_id = 4)
    // action_result_id: 4=Successful, 16=Draw Foul, 19=Deflected, 10=Unsuccessful, 17=Stolen
    const one_vs_one_attacking = [
        { action_id: 4, action_result_id: 4, name: 'Successful', value: 0, color: '#1F712D', tags: [] },
        { action_id: 4, action_result_id: 16, name: 'Draw Foul', value: 0, color: '#185522', tags: [] },
        { action_id: 4, action_result_id: 19, name: 'Deflected', value: 0, color: 'grey', tags: [] },
        { action_id: 4, action_result_id: 10, name: 'Unsuccessful', value: 0, color: '#FF8C00', tags: [] },
        { action_id: 4, action_result_id: 17, name: 'Stolen', value: 0, color: '#A50707', tags: [] }
    ];

    // 1v1 Defending - player's own defensive actions
    // action_id: 10=Interception, 11=Clearance, 12=Tackle, 13=Blocked, 14=Deflected, 17=Super Save, 18=Air Challenge, 19=Ground Challenge
    const one_vs_one_defending = [
        { action_id: 10, name: 'Interception', value: 0, color: '#1F712D', tags: [] },
        { action_id: 11, name: 'Clearance', value: 0, color: '#185522', tags: [] },
        { action_id: 12, name: 'Tackle', value: 0, color: '#05389E', tags: [] },
        { action_id: 13, name: 'Blocked', value: 0, color: '#148DB7', tags: [] },
        { action_id: 14, name: 'Deflected', value: 0, color: 'grey', tags: [] },
        { action_id: 17, name: 'Super Save', value: 0, color: '#F1C40F', tags: [] },
        { action_id: 18, name: 'Air Challenge', value: 0, color: '#FF8C00', tags: [] },
        { action_id: 19, name: 'Ground Challenge', value: 0, color: '#A50707', tags: [] }
    ];

    const passing_accuaracy = [
        { action_names: 'Pass', action_type_names: 'Key Pass,Pass For a Shot,Short Pass,Through Pass,Long Pass,Throw-In', action_result_names: 'Successful', value: 0, color: '#1F712D', tags: [] },
        { action_names: 'Pass', action_type_names: 'Key Pass,Pass For a Shot,Short Pass,Through Pass,Long Pass,Throw-In', action_result_names: 'Offside', value: 0, color: 'black', tags: [] },
        { action_names: 'Pass', action_type_names: 'Key Pass,Pass For a Shot,Short Pass,Through Pass,Long Pass,Throw-In', action_result_names: 'Deflected', value: 0, color: 'grey', tags: [] },
        { action_names: 'Pass', action_type_names: 'Key Pass,Pass For a Shot,Short Pass,Through Pass,Long Pass,Throw-In', action_result_names: 'Bad Pass', value: 0, color: '#A50707', tags: [] }
    ];

    const passing_types = [
        { action_names: 'Pass', action_type_names: 'Key Pass', action_result_names: '', value: 0, color: '#05389E', tags: [] },
        { action_names: 'Pass', action_type_names: 'Pass For a Shot', action_result_names: '', value: 0, color: '#148DB7', tags: [] },
        { action_names: 'Pass', action_type_names: 'Short Pass', action_result_names: '', value: 0, color: '#1F712D', tags: [] },
        { action_names: 'Pass', action_type_names: 'Through Pass', action_result_names: '', value: 0, color: '#FF8C00', tags: [] },
        { action_names: 'Pass', action_type_names: 'Long Pass', action_result_names: '', value: 0, color: '#F1C40F', tags: [] },
        { action_names: 'Pass', action_type_names: 'Throw-In', action_result_names: '', value: 0, color: '#A50707', tags: [] },
        { action_names: 'Assist', action_type_names: '', action_result_names: '', value: 0, color: '#9B59B6', tags: [] }
    ];

    const intercepting_types = [
        { action_names: 'Interception', action_type_names: 'Key Pass', action_result_names: 'Bad Pass,Deflected,Blocked,Stolen', value: 0, color: '#05389E', tags: [] },
        { action_names: 'Interception', action_type_names: 'Through Pass', action_result_names: 'Bad Pass,Deflected,Blocked,Stolen', value: 0, color: '#FF8C00', tags: [] },
        { action_names: 'Interception', action_type_names: 'Short Pass', action_result_names: 'Bad Pass,Deflected,Blocked,Stolen', value: 0, color: '#1F712D', tags: [] },
        { action_names: 'Interception', action_type_names: 'Long Pass', action_result_names: 'Bad Pass,Deflected,Blocked,Stolen', value: 0, color: '#F1C40F', tags: [] },
        { action_names: 'Interception', action_type_names: 'Throw-In', action_result_names: 'Bad Pass,Deflected,Blocked,Stolen', value: 0, color: '#A50707', tags: [] }
    ];

    const crossing_accuaracy = [
        { action_names: 'Cross', action_type_names: 'Right, Left', action_result_names: 'Successful', value: 0, color: '#1F712D', tags: [] },
        { action_names: 'Cross', action_type_names: 'Right, Left', action_result_names: 'Cleared', value: 0, color: '#148DB7', tags: [] },
        { action_names: 'Cross', action_type_names: 'Right, Left', action_result_names: 'Offside', value: 0, color: 'black', tags: [] },
        { action_names: 'Cross', action_type_names: 'Right, Left', action_result_names: 'Blocked', value: 0, color: '#FF8C00', tags: [] },
        { action_names: 'Cross', action_type_names: 'Right, Left', action_result_names: 'Unsuccessful', value: 0, color: '#A50707', tags: [] }
    ];

    const shooting_accuaracy = [
        { action_names: 'Shot', action_type_names: '', action_result_names: 'Goal', value: 0, color: '#148DB7', tags: [] },
        { action_names: 'Shot', action_type_names: '', action_result_names: 'On Target', value: 0, color: '#1F712D', tags: [] },
        { action_names: 'Shot', action_type_names: '', action_result_names: 'Blocked', value: 0, color: '#FF8C00', tags: [] },
        { action_names: 'Shot', action_type_names: '', action_result_names: 'Saved', value: 0, color: 'grey', tags: [] },
        { action_names: 'Shot', action_type_names: '', action_result_names: 'Off Target', value: 0, color: '#cc0000', tags: [] }
    ];

    const defensive_stops_types = [
        { action_names: 'Tackle', action_type_names: '', action_result_names: 'Stolen', value: 0, color: '#05389E', tags: [] },
        { action_names: 'Clearance', action_type_names: '', action_result_names: 'Cleared', value: 0, color: '#148DB7', tags: [] },
        { action_names: 'Blocked', action_type_names: '', action_result_names: 'Blocked', value: 0, color: '#FF8C00', tags: [] },
        { action_names: 'Deflected', action_type_names: '', action_result_names: 'Deflected', value: 0, color: 'grey', tags: [] }
    ];

    const one_vs_one_attacking_items = []
    const one_vs_one_defending_items = []
    const passing_accuaracy_items = []
    const passing_types_items = []
    const intercepting_types_items = []
    const crossing_accuaracy_items = []
    const shooting_accuaracy_items = []
    const defensive_stops_types_items = []

    playerData.forEach((ses) => {
        // 1v1 Attacking - match by action_id and action_result_id
        const one_vs_one_attacking_found = one_vs_one_attacking.find(act =>
            act.action_id === ses.action_id && act.action_result_id === ses.action_result_id
        )
        if (one_vs_one_attacking_found) {
            one_vs_one_attacking_found.value += 1;
            one_vs_one_attacking_found.tags.push(ses);
            one_vs_one_attacking_items.push(ses)
        }

        // 1v1 Defending - player's own defensive actions (action_ids: 10, 11, 12, 13, 14, 17, 18, 19)
        const one_vs_one_defending_found = one_vs_one_defending.find(act =>
            act.action_id === ses.action_id
        )
        if (one_vs_one_defending_found) {
            one_vs_one_defending_found.value += 1;
            one_vs_one_defending_found.tags.push(ses);
            one_vs_one_defending_items.push(ses)
        }

        const passing_accuaracy_found = passing_accuaracy.find(act => act.action_names.includes(ses.action_name) && act.action_type_names.includes(ses.action_type_name) && act.action_result_names.includes(ses.action_result_name))
        if (passing_accuaracy_found) {
            passing_accuaracy_found.value += 1;
            passing_accuaracy_found.tags.push(ses);
            passing_accuaracy_items.push(ses);
        }
        const passing_types_found = passing_types.find(act => act.action_names.includes(ses.action_name) && act.action_type_names.includes(ses.action_type_name))
        if (passing_types_found) {
            passing_types_found.value += 1;
            passing_types_found.tags.push(ses);
            passing_types_items.push(ses);
        }
        // Count assists as successful passes (Passing Accuracy) and as a passing type (Pass for a Goal)
        if (ses.action_name === 'Assist') {
            passing_accuaracy[0].value += 1;
            passing_accuaracy[0].tags.push(ses);
            passing_accuaracy_items.push(ses);
            const assist_type = passing_types.find(act => act.action_names === 'Assist');
            if (assist_type) {
                assist_type.value += 1;
                assist_type.tags.push(ses);
                passing_types_items.push(ses);
            }
        }
        const intercepting_types_found = intercepting_types.find(act => act.action_names.includes(ses.action_name) && act.action_type_names.includes(ses.action_type_name) && act.action_result_names.includes(ses.action_result_name))
        if (intercepting_types_found) {
            intercepting_types_found.value += 1;
            intercepting_types_found.tags.push(ses);
            intercepting_types_items.push(ses);
        }

        const crossing_accuaracy_found = crossing_accuaracy.find(act => act.action_names.includes(ses.action_name) && act.action_type_names.includes(ses.action_type_name) && act.action_result_names.includes(ses.action_result_name))
        if (crossing_accuaracy_found) {
            crossing_accuaracy_found.value += 1;
            crossing_accuaracy_found.tags.push(ses);
            crossing_accuaracy_items.push(ses);
        }

        const shooting_accuaracy_found = shooting_accuaracy.find(act => act.action_names.includes(ses.action_name) && act.action_result_names.includes(ses.action_result_name))
        if (shooting_accuaracy_found) {
            shooting_accuaracy_found.value += 1;
            shooting_accuaracy_found.tags.push(ses);
            shooting_accuaracy_items.push(ses);
        }
        const defensive_stops_types_found = defensive_stops_types.find(act => act.action_names.includes(ses.action_name) && act.action_result_names.includes(ses.action_result_name))
        if (defensive_stops_types_found) {
            defensive_stops_types_found.value += 1;
            defensive_stops_types_found.tags.push(ses);
            defensive_stops_types_items.push(ses);
        }
    });

    return [
        {
            title: '1v1 Attacking',
            total: getSum(one_vs_one_attacking.map(act => act.value)),
            tags: one_vs_one_attacking_items,
            seriesBar: one_vs_one_attacking
                .filter(act => act.value > 0)
                .map(act => ({
                    name: act.name,
                    data: act.value,
                    tags: act.tags,
                    color: act.color
                }))
        }, //1
        {
            title: '1v1 Defending',
            total: getSum(one_vs_one_defending.map(act => act.value)),
            tags: one_vs_one_defending_items,
            seriesBar: one_vs_one_defending
                .filter(act => act.value > 0)
                .map(act => ({
                    name: act.name,
                    data: act.value,
                    tags: act.tags,
                    color: act.color
                }))
        }, //2
        {
            title: 'Passing Accuracy',
            total: getSum(passing_accuaracy.map(act => act.value)),
            tags: passing_accuaracy_items,
            seriesBar: passing_accuaracy
                .filter(act => act.value > 0)
                .map(act => ({
                    name: act.action_result_names === 'Bad Pass' ? 'Unsuccessful' : act.action_result_names,
                    data: act.value,
                    tags: act.tags,
                    color: act.color
                }))
        }, //3
        {
            title: 'Passing Types',
            total: getSum(passing_types.map(act => act.value)),
            tags: passing_types_items,
            seriesBar: passing_types
                .filter(act => act.value > 0)
                .map(act => ({
                    name: act.action_names === 'Assist' ? 'Pass for a Goal' : act.action_type_names === 'Pass For a Shot' ? 'Pass for a Shot' : act.action_type_names,
                    data: act.value,
                    tags: act.tags,
                    color: act.color
                }))
        },//6
        {
            title: 'Crossing Accuracy',
            total: getSum(crossing_accuaracy.map(act => act.value)),
            tags: crossing_accuaracy_items,
            seriesBar: crossing_accuaracy
                .filter(act => act.value > 0)
                .map(act => ({
                    name: act.action_result_names === 'Unsuccessful' ? 'Unsucessful' : act.action_result_names,
                    data: act.value,
                    tags: act.tags,
                    color: act.color
                }))
        },  //7
        {
            title: 'Shooting & Finishing',
            total: getSum(shooting_accuaracy.map(act => act.value)),
            tags: shooting_accuaracy_items,
            seriesBar: shooting_accuaracy
                .filter(act => act.value > 0)
                .map(act => ({
                    name: act.action_result_names,
                    data: act.value,
                    tags: act.tags,
                    color: act.color
                }))
        }, //4
        {
            title: 'Intercepting Types',
            total: getSum(intercepting_types.map(act => act.value)),
            tags: intercepting_types_items,
            seriesBar: intercepting_types
                .filter(act => act.value > 0)
                .map(act => ({
                    name: act.action_type_names,
                    data: act.value,
                    tags: act.tags,
                    color: act.color
                }))
        },//5
        {
            title: 'Defensive Stops',
            total: getSum(defensive_stops_types.map(act => act.value)),
            tags: defensive_stops_types_items,
            seriesBar: defensive_stops_types
                .filter(act => act.value > 0)
                .map(act => ({
                    name: act.action_result_names,
                    data: act.value,
                    tags: act.tags,
                    color: act.color
                }))
        } //8
    ]
}

// ============================================================================
// S3 URL Helper Functions - Centralized image URL handling
// ============================================================================

const S3_BASE_URL = process.env.REACT_APP_S3_URI || '';

/**
 * Converts a relative path to a full S3 URL
 * @param {string} path - The relative path or existing URL
 * @returns {string|null} - Full S3 URL or null if path is falsy
 */
export const toS3Url = (path) => {
    if (!path) return null;
    // If already a full URL, return as-is
    if (path.startsWith('http://') || path.startsWith('https://')) {
        return path;
    }
    // Remove leading slashes and combine with base URL
    const cleanPath = path.replace(/^\/+/, '');
    return S3_BASE_URL ? `${S3_BASE_URL.replace(/\/+$/, '')}/${cleanPath}` : cleanPath;
};

/**
 * Processes an object's image fields to add S3 URL prefix
 * @param {Object} data - Object with potential image fields
 * @param {string[]} imageFields - Array of field names to process
 * @returns {Object} - Same object with processed image URLs
 */
export const processImageUrls = (data, imageFields = ['player_image', 'team_image', 'image', 'home_team_logo', 'away_team_logo', 'sponsor_logo']) => {
    if (!data) return data;

    const processed = { ...data };
    imageFields.forEach(field => {
        if (processed[field]) {
            processed[field] = toS3Url(processed[field]);
        }
    });
    return processed;
};

/**
 * Processes an array of objects to add S3 URL prefix to image fields
 * @param {Array} dataArray - Array of objects with potential image fields
 * @param {string[]} imageFields - Array of field names to process
 * @returns {Array} - Same array with processed image URLs
 */
export const processArrayImageUrls = (dataArray, imageFields) => {
    if (!Array.isArray(dataArray)) return dataArray;
    return dataArray.map(item => processImageUrls(item, imageFields));
};

// ============================================================================
// Player Physical Attributes Constants
// ============================================================================

export const PLAYER_HEIGHT_MIN = 100;
export const PLAYER_HEIGHT_MAX = 205;
export const PLAYER_WEIGHT_MIN = 20;
export const PLAYER_WEIGHT_MAX = 110;

export const getHeightOptions = () =>
    Array.from({ length: PLAYER_HEIGHT_MAX - PLAYER_HEIGHT_MIN + 1 }, (_, i) => PLAYER_HEIGHT_MIN + i);

export const getWeightOptions = () =>
    Array.from({ length: PLAYER_WEIGHT_MAX - PLAYER_WEIGHT_MIN + 1 }, (_, i) => PLAYER_WEIGHT_MIN + i);
